/**
 * Generates a QR code from invoice details
 * @param {Object} qrParams - QR code generation parameters
 * @param {string} qrParams.irn - Invoice Reference Number
 * @param {string} qrParams.certificate - Encryption certificate
 * @param {string} qrParams.publicKey - Public key for encryption
 * @param {number} [qrParams.size=300] - QR code size in pixels
 * @param {string} [qrParams.fgColor="#000000"] - Foreground color
 * @param {string} [qrParams.bgColor="#FFFFFF"] - Background color
 * @param {string} [qrParams.logo] - Logo URL
 * @param {number} [qrParams.logoSizeRatio=0.2] - Logo size ratio
 * @returns {Promise<Object>} QR code generation result
 */
 async function generateInvoiceQRCode(qrParams) {
  try {
    // Call your API endpoint or service
    const response = await fetch('/api/qrcode/generate', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(qrParams)
    });

    // Check HTTP status before attempting to parse JSON.
    // A non-OK response (4xx / 5xx) may return an HTML error page
    // which causes "JSON.parse: unexpected character at line 1 column 1".
    if (!response.ok) {
      const text = await response.text();
      throw new Error(`Server error ${response.status}: ${text.substring(0, 300)}`);
    }

    const result = await response.json();

    // Backend returns { status, message, data: { qrCodeDataUrl, encryptedBase64, hasBuffer } }
    if (result.status === 'error') {
      throw new Error(result.message || 'QR code generation failed on server');
    }

    return {
      success: true,
      data: {
        qrCodeDataUrl: result.data?.qrCodeDataUrl,
        encryptedBase64: result.data?.encryptedBase64,
        hasBuffer: result.data?.hasBuffer ?? false,
      },
    };
  } catch (error) {
    return {
      success: false,
      error: error.message,
    };
  }
}


/**
 * Decrypts an encrypted invoice payload
 * @param {Object} decryptParams - Decryption parameters
 * @param {string} decryptParams.iv_hex - Initialization vector in hex
 * @param {string} decryptParams.pub - Public component
 * @param {string} decryptParams.ciphertext - Encrypted data
 * @param {string} decryptParams.api_key - API key for decryption
 * @returns {Promise<Object>} Decrypted invoice data
 */
 async function decryptInvoiceData(decryptParams) {
  try {
    const response = await fetch('/api/qrcode/decrypt', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(decryptParams)
    });

    if (!response.ok) {
      const text = await response.text();
      throw new Error(`Server error ${response.status}: ${text.substring(0, 300)}`);
    }

    const result = await response.json();
    return {
      success: true,
      data: result,
    };
  } catch (error) {
    return {
      success: false,
      error: error.message,
    };
  }
}

/**
 * Complete QR generation and decryption workflow
 * @param {Object} qrParams - QR code parameters
 * @param {Object} decryptParams - Decryption parameters
 * @returns {Promise<Object>} Combined results
 */
 async function processInvoiceQRAndDecrypt(qrParams, decryptParams) {
  try {
    const qrResult = await generateInvoiceQRCode(qrParams);
    const decryptResult = await decryptInvoiceData(decryptParams);

    return {
      success: true,
      qrCode: qrResult,
      invoice: decryptResult,
    };
  } catch (error) {
    return {
      success: false,
      error: error.message,
    };
  }
}

module.exports = { generateInvoiceQRCode, decryptInvoiceData, processInvoiceQRAndDecrypt };